/** @copyright 2025 Cooltrax Pty Ltd. All rights reserved.
 */

import React, { useState, useCallback } from 'react'

import { FiPlus, FiSave, FiX } from 'react-icons/fi'
import { useNavigate } from 'react-router-dom'

import { PageHeader } from '@/components/ui'
// import PageHeader from '@/components/ui/PageHeader/PageHeader'
// import PageMenu, { MenuAction } from '@/components/ui/PageMenu/PageMenu'
// import { TruncateText } from '@/components/ui-sdk'
import { useDashboard } from '@/hooks'
import type { DashboardType } from '@/types/dashboard-types'

import { ColorPicker } from './components/ColorPicker'
import { IconPicker } from './components/IconPicker'
import { validateDashboardTitle } from './validation'

type FormErrors = { title?: string }

export const Form = ({
  errors,
  dashboard,
  setDashboard,
  actions,
}: {
  errors: FormErrors
  dashboard: DashboardType
  setDashboard: (dashboard: DashboardType) => void
  actions?: React.ReactNode
}) => {
  const dashboardContext = useDashboard()
  const currentDefault = dashboardContext?.configuration?.dashboards?.find((p) => p.isDefault)?.title

  return (
    <div className="card bg-base-100 mx-auto w-full max-w-3xl shadow">
      <div className="card-body gap-6">
        <div className="form-control flex w-full flex-col gap-1">
          <label htmlFor="dashboard-title" className="label">
            <span className="label-text">Dashboard Name *</span>
          </label>
          <input
            id="dashboard-title"
            type="text"
            autoFocus
            placeholder="My Custom Dashboard"
            className={`input input-bordered w-full focus:outline-none focus:shadow-none focus:border-primary ${errors.title ? 'input-error' : ''}`}
            value={dashboard.title}
            onChange={(e) => setDashboard({ ...dashboard, title: e.target.value })}
          />
          {errors.title && (
            <span role="alert" className="label-text-alt text-error">
              {errors.title}
            </span>
          )}
        </div>

        <div className="form-control flex w-full flex-col gap-1">
          <label htmlFor="dashboard-description" className="label">
            <span className="label-text">Description</span>
          </label>
          <textarea
            id="dashboard-description"
            className="textarea textarea-bordered h-24 w-full focus:outline-none focus:shadow-none focus:border-primary"
            placeholder="Optional description to help identify this dashboard"
            value={dashboard.description}
            onChange={(e) => setDashboard({ ...dashboard, description: e.target.value })}
          />
        </div>

        <fieldset className="flex flex-col gap-4">
          <legend className="mb-1 text-sm font-semibold">Appearance</legend>
          <div className="form-control flex flex-col gap-1">
            <span className="label label-text">Icon</span>
            <IconPicker
              value={dashboard.icon}
              color={dashboard.iconColor}
              onChange={(icon) => setDashboard({ ...dashboard, icon })}
            />
          </div>
          <div className="form-control flex flex-col gap-1">
            <span className="label label-text">Icon Color</span>
            <ColorPicker
              value={dashboard.iconColor}
              onChange={(iconColor) => setDashboard({ ...dashboard, iconColor })}
            />
          </div>
        </fieldset>

        <label className="flex cursor-pointer items-center justify-between gap-4 rounded-lg border border-base-300 p-4">
          <span className="flex flex-col gap-0.5">
            <span className="label-text font-medium">Set as Default Dashboard</span>
            <span className="label-text-alt text-base-content/70">
              {dashboard.isDefault && currentDefault
                ? `This will replace ${currentDefault} as the default dashboard.`
                : 'The default dashboard is shown first when visiting dashboards.'}
            </span>
          </span>
          <input
            type="checkbox"
            className="toggle toggle-primary shrink-0"
            checked={dashboard.isDefault}
            onChange={(e) => setDashboard({ ...dashboard, isDefault: e.target.checked })}
          />
        </label>

        {actions && <div className="flex justify-end gap-2">{actions}</div>}
      </div>
    </div>
  )
}

/**
 * Create Dashboard page for building new custom dashboards
 */
const CreateDashboard: React.FC = () => {
  const dashboardContext = useDashboard()
  const navigate = useNavigate()

  const [dashboard, setDashboard] = useState<DashboardType>({
    title: '',
    slug: '',
    parameters: {},
    insights: [],
    isDefault: false,
  })
  const [errors, setErrors] = useState<FormErrors>({})

  const { dashboards = [] } = dashboardContext.configuration || {}

  const validateForm = useCallback(() => {
    const title = validateDashboardTitle(dashboard.title, dashboards)
    const newErrors: FormErrors = title ? { title } : {}
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }, [dashboard.title, dashboards])

  const handleSave = useCallback(() => {
    if (!validateForm()) return
    dashboardContext.addDashboard({ dashboard: { ...dashboard, title: dashboard.title.trim() } })
  }, [dashboard, dashboardContext.addDashboard])

  const handleCancel = useCallback(() => {
    navigate('/dashboards')
  }, [navigate])

  return (
    <div className="p-4 space-y-6">
      <PageHeader title="Create New Dashboard" icon={<FiPlus />} />
      <form
        onSubmit={(e) => {
          e.preventDefault()
          handleSave()
        }}
      >
        <Form
          dashboard={dashboard}
          setDashboard={setDashboard}
          errors={errors}
          actions={
            <>
              <button type="button" className="btn btn-ghost gap-2" onClick={handleCancel}>
                <FiX />
                Cancel
              </button>
              <button type="submit" className="btn btn-primary gap-2" disabled={!dashboard.title.trim()}>
                <FiSave />
                Create Dashboard
              </button>
            </>
          }
        />
      </form>
    </div>
  )
}

export default CreateDashboard
